import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import {DollarSign, Edit2, Save, X} from 'lucide-react'
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"

interface Taxa {
  _id: string
  nome: string
  valor: string
  ativa: boolean
}

export default function GerenciarTaxas() {
  const [taxas, setTaxas] = useState<Taxa[]>([])
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState("")

  useEffect(() => {
    loadTaxas()
  }, [])

  const loadTaxas = async () => {
    try {
      const result = await lumi.entities.taxas.list()
      setTaxas(result.list || [])
    } catch (error) {
      console.error("Erro ao carregar taxas:", error)
      toast.error("Erro ao carregar taxas")
    }
  }

  const formatValor = (value: string) => {
    const numbers = value.replace(/\D/g, "")
    const amount = parseFloat(numbers) / 100
    return amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
  }

  const handleEdit = (taxa: Taxa) => {
    setEditingId(taxa._id)
    setEditValue(taxa.valor)
  }

  const handleSave = async (taxaId: string) => {
    try {
      await lumi.entities.taxas.update(taxaId, {
        valor: editValue,
        updatedAt: new Date().toISOString()
      })
      toast.success("Taxa atualizada com sucesso")
      setEditingId(null)
      loadTaxas()
      
      // Disparar evento para notificar outros componentes
      window.dispatchEvent(new CustomEvent("taxasAtualizadas"))
    } catch (error) {
      console.error("Erro ao atualizar taxa:", error)
      toast.error("Erro ao atualizar taxa")
    }
  }

  const handleCancel = () => {
    setEditingId(null)
    setEditValue("")
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg p-4 sm:p-6 lg:p-8"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-lg">
          <DollarSign className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Gerenciar Taxas</h2>
          <p className="text-sm text-gray-600">Configure os valores das taxas dos vendedores</p>
        </div>
      </div>

      <div className="space-y-4">
        {taxas.map((taxa) => (
          <motion.div
            key={taxa._id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {taxa.nome}
                </h3>
                {editingId === taxa._id ? (
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(formatValor(e.target.value))}
                      className="px-4 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="R$ 0,00"
                    />
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleSave(taxa._id)}
                      className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    >
                      <Save className="w-5 h-5" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleCancel}
                      className="p-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </motion.button>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <span className="text-2xl font-bold text-green-600">
                      {taxa.valor}
                    </span>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleEdit(taxa)}
                      className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      <Edit2 className="w-5 h-5" />
                    </motion.button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Nota:</strong> Os valores das taxas serão aplicados automaticamente quando
          selecionados no cadastro de vendedores.
        </p>
      </div>
    </motion.div>
  )
}
